import React, { createContext, useContext, useEffect, useState } from 'react'
import { authAPI, PASSWORD_CHANGE_REQUIRED_EVENT } from '../api/client'
import type { AuthUser } from '../api/types'

/**
 * F08-001 (2026-05-19): the previous implementation persisted the JWT
 * access token in localStorage, where any XSS payload could lift it
 * and exfiltrate the session. The migrated flow:
 *
 *   1. On login, the backend sets an HttpOnly + Secure + SameSite=Strict
 *      cookie (`hpanel_access_token`). The cookie is invisible to JS,
 *      so XSS can't read it.
 *   2. The frontend stores only the `authUser` profile (username, role,
 *      etc.) — not the credential itself — and relies on the cookie
 *      being sent automatically by `credentials: 'include'`.
 *   3. On every page load, the AuthContext attempts `authAPI.me()`:
 *      if the cookie is valid, we hydrate `user`; otherwise we land
 *      unauthenticated and the router shows /login.
 *   4. Logout calls `authAPI.logout()` which deletes the cookie
 *      server-side. The browser drops it; subsequent /api/auth/me
 *      returns 401 and we redirect to /login.
 *
 * `token` is kept on the context as `null` for backwards compatibility
 * with consumers that destructure it; nothing meaningful flows through
 * it anymore.
 */

interface AuthContextType {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  /** Resolves with the authenticated profile so callers can route on role. */
  login: (username: string, password: string, totpCode?: string) => Promise<AuthUser>
  logout: () => Promise<void>
  register: (email: string, username: string, password: string) => Promise<void>
  /** Re-read the profile from the server (e.g. after a password change). */
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

const USER_CACHE_KEY = 'authUser'

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    // F08-001: hydrate from authAPI.me() — the cookie is the source
    // of truth. The localStorage `authUser` cache is JUST for UI
    // pre-render (avoids the "logged out flash"); the server's
    // response decides what's authoritative.
    const cached = localStorage.getItem(USER_CACHE_KEY)
    if (cached) {
      try {
        setUser(JSON.parse(cached) as AuthUser)
      } catch {
        localStorage.removeItem(USER_CACHE_KEY)
      }
    }

    // Verify against the server. If the cookie's still valid, we
    // refresh the cache; otherwise we drop the cached profile.
    authAPI
      .me()
      .then((fresh) => {
        setUser(fresh)
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(fresh))
      })
      .catch(() => {
        setUser(null)
        localStorage.removeItem(USER_CACHE_KEY)
      })
      .finally(() => setIsLoading(false))
  }, [])

  // The API says this account must change its password (e.g. an admin
  // reset it while the user was signed in): reflect that, so ProtectedRoute
  // shows the change-password page.
  useEffect(() => {
    const onRequired = () =>
      setUser((u) => (u ? { ...u, must_change_password: true } : u))
    window.addEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, onRequired)
    return () => window.removeEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, onRequired)
  }, [])

  const refreshUser = async () => {
    const fresh = await authAPI.me()
    setUser(fresh)
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(fresh))
  }

  const login = async (username: string, password: string, totpCode?: string) => {
    setIsLoading(true)
    try {
      // Backend sets the HttpOnly cookie on the response; we ignore
      // the access_token field in the body — it's only there for
      // legacy SPAs that still use Bearer.
      await authAPI.login({ username, password, totp_code: totpCode })

      // Fetch user details via the central client. The cookie just
      // set on the login response is sent on this follow-up call.
      const userData = await authAPI.me()
      setUser(userData)
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData))
      return userData
    } catch (error) {
      setUser(null)
      localStorage.removeItem(USER_CACHE_KEY)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    // Keep the session visible until the server confirms cookie removal.
    await authAPI.logout()
    setUser(null)
    localStorage.removeItem(USER_CACHE_KEY)
  }

  const register = async (email: string, username: string, password: string) => {
    setIsLoading(true)
    try {
      await authAPI.register({ email, username, password })
      // Registration successful — log in to mint a fresh session cookie.
      await login(username, password)
    } catch (error) {
      setUser(null)
      localStorage.removeItem(USER_CACHE_KEY)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const value: AuthContextType = {
    user,
    // F08-001: token is no longer carried in JS — the HttpOnly cookie
    // is the credential. Kept as `null` for backwards compat with
    // consumers that destructure { token }.
    token: null,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    register,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
